import { HttpClient } from '@angular/common/http';
import { environment } from '@env';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { Invitation } from '@shared/models/invitation.model';

@Injectable({
    providedIn: 'root',
})
export class InvitationService {

    /** Emits the current number of invitations for the active user */
    private invitationCountSubject = new BehaviorSubject<number>(0);
    /** Public observable of the invitation count */
    public readonly invitationCount$ = this.invitationCountSubject.asObservable();

    constructor(private http: HttpClient) { }
    /**
     * Gets a list of invitations for a given user ID
     * @param {number} userId - The {@link id} of the user
     * @return {Observable<Invitation[]>} - The invitation list
     */
    getUserInvitations(userId: number): Observable<Invitation[]> {
        return this.http.get<Invitation[]>(environment.endpoints.userInvitationsList(userId)).pipe(
            map(invitations => {
                return invitations.map((invitation) => new Invitation().deserialize(invitation));
            }),
        );
    }

    /**
     * Accept an invitation for a given user ID and invitation ID
     * @param {number} userId - The {@link id} of the user
     * @param {number} invitationId - The {@link id} of the invitation
     * @return {Observable<void>} - Nothing is returned after accepting an invitation
     */
    acceptUserInvitation(userId: number, invitationId: number): Observable<void> {
        return this.http
            .post<void>(environment.endpoints.userInvitationAccept(userId, invitationId), {})
            .pipe(tap(() => this.refreshInvitationCount(userId)));
    }

    /**
     * Reject an invitation for a given user ID and invitation ID
     * @param {number} userId - The {@link id} of the user
     * @param {number} invitationId - The {@link id} of the invitation
     * @return {Observable<void>} - Nothing is returned after rejecting an invitation
     */
    rejectUserInvitation(userId: number, invitationId: number): Observable<void> {
        return this.http
            .post<void>(environment.endpoints.userInvitationReject(userId, invitationId), {})
            .pipe(tap(() => this.refreshInvitationCount(userId)));
    }
    /**
     * Refresh the cached invitation count and emit it on {@link invitationCount$}.
     * Use this after login or when invitations are modified elsewhere.
     */
    refreshInvitationCount(userId: number): void {
        this.getUserInvitations(userId).subscribe({
            next: (invitations) => this.invitationCountSubject.next(invitations.length),
            error: (err) => {
                console.error('Error refreshing invitation count', err);
                this.invitationCountSubject.next(0);
            },
        });
    }
}
